import React from "react";
import i18n from "@/i18n";
import {
  Check,
  Coins,
  CreditCard,
  Lock,
  RefreshCcwDot,
  RefreshCwOff,
  TriangleAlert,
} from "lucide-react";

import useDialogModal from "@/hooks/useDialogModal";
import useSubscription from "@/hooks/subscriptions/useSubscription";
import useUpdateSubscription from "@/hooks/subscriptions/useUpdateSubscription";
import useSubscriptionRefillPrepaid from "@/hooks/subscriptions/useSubscriptionRefillPrepaid";
import useCompanyIdFromUrl from "@/hooks/companies/useCompanyIdFromUrl";
import useDefaultPaymentMethod from "@/hooks/subscriptions/useDefaultPaymentMethod";
import useIsAllowed from "@/hooks/permissions/useIsAllowed";

import PrepaidIndicator from "@/components/subscriptions/prepaidIndicator";
import { Button } from "@/components/ui/button";
import PaymentMethods from "@/components/subscriptions/paymentMethods";
import BillingInvoiceReceiverDetails from "@/components/subscriptions/billingInvoiceReceiverDetails";

import { cn } from "../utils";
import { Trans } from "react-i18next";
import { Skeleton } from "../ui/skeleton";

const { t } = i18n;

const PrepaidPanel = ({ pendingAdjustment = 0, className }) => {
  const companyId = useCompanyIdFromUrl();
  const { dialogElement, prompt, confirm, alert } = useDialogModal();

  const { data: isAllowedQuery, isLoading: isLoadingPermissions } =
    useIsAllowed("RESOURCE_SUBSCRIPTIONS", "SUBSCRIPTIONS--CREATE");
  const hasPermission = isAllowedQuery?.data?.isAllowed;

  const { data: subscriptionQuery, isLoading: isLoadingSubscription } =
    useSubscription();
  const subscription = subscriptionQuery?.data;

  const {
    isLoading: isLoadingDefaultPaymentMethod,
    data: defaultPaymentMethod,
  } = useDefaultPaymentMethod();

  const isInvoiced = subscription?.stripeData?.metadata?.isInvoiced === "true";

  const { mutateAsync: updateSubscription } = useUpdateSubscription(companyId);

  const { mutateAsync: refillPrepaid, isPending: isRefillingPrepaid } =
    useSubscriptionRefillPrepaid(companyId);

  if (
    isLoadingSubscription ||
    isLoadingDefaultPaymentMethod ||
    isLoadingPermissions ||
    !subscription
  ) {
    return <Skeleton className="h-[82px] w-full" />;
  }

  const hasValidPaymentMethod = isInvoiced || !!defaultPaymentMethod;
  const prepaidSelectedAmount = subscription.prepaidSelectedAmount || 0;
  const { prepaidAutoRefill, prepaidAmount = 0 } = subscription;
  const willOverdraft = pendingAdjustment + prepaidAmount < 0;
  const hasSelectedAmount = prepaidSelectedAmount > 0;
  const showPurchaseButton = hasSelectedAmount && hasValidPaymentMethod;

  const onClickSelectAmount = async () =>
    prompt(
      t("subscriptions.prepaid.pot"),
      ({ onSubmit }) => (
        <div className="flex w-full flex-col gap-2">
          {[0, 100, 250, 500, 1000, 1500, 2000, 2500, 3000].map((amount) => (
            <Button
              className="w-full"
              variant={
                amount === prepaidSelectedAmount ? "outline-primary" : "outline"
              }
              key={amount}
              onClick={() => onSubmit({ prepaidSelectedAmount: amount })}
            >
              {amount === prepaidSelectedAmount ? <Check size={16} /> : null}+
              {amount} SEK
            </Button>
          ))}
        </div>
      ),
      {
        size: "xs",
        submitText: null,
        onSubmit: updateSubscription,
      },
    );

  return (
    <>
      <div
        className={cn(
          className,
          "flex items-center justify-between gap-4 bg-background",
          { "border border-destructive": willOverdraft },
        )}
      >
        <div>
          <PrepaidIndicator
            currentAmount={subscription.prepaidAmount}
            pendingAdjustment={pendingAdjustment}
          />
        </div>
        <div className="flex flex-col justify-end gap-2">
          {hasPermission ? (
            <div className="flex items-center gap-2">
              <div className="flex items-center">
                <Button
                  variant="outline"
                  onClick={onClickSelectAmount}
                  className="rounded-full rounded-r-none border-r-0"
                >
                  <Coins size={18} />
                  {hasSelectedAmount
                    ? `+${prepaidSelectedAmount} SEK`
                    : t("subscriptions.prepaid.placeholder.select_amount")}
                </Button>
                <Button
                  disabled={!hasSelectedAmount}
                  onClick={async () => {
                    if (
                      !prepaidAutoRefill &&
                      !(await confirm(
                        t("subscriptions.prepaid.autorefill.activate"),
                        t("subscriptions.prepaid.autorefill.confirmation", {
                          amount: prepaidSelectedAmount,
                          amountTenPercent: prepaidSelectedAmount / 10,
                        }),
                        {
                          icon: <RefreshCcwDot />,
                          submitText: t("generic.activate"),
                        },
                      ))
                    ) {
                      return;
                    }

                    updateSubscription({
                      prepaidAutoRefill: !prepaidAutoRefill,
                    });
                  }}
                  className={cn("rounded-full rounded-l-none", {
                    "border-emerald-500 bg-emerald-100": prepaidAutoRefill,
                  })}
                  title={
                    prepaidAutoRefill ? (
                      <Trans
                        i18nKey={"subscriptions.prepaid.autorefill.enable"}
                        values={{ amount: prepaidSelectedAmount / 10 }}
                      />
                    ) : (
                      t("subscriptions.prepaid.autorefill.disable")
                    )
                  }
                  variant={prepaidAutoRefill ? "outline-success" : "outline"}
                >
                  {prepaidAutoRefill ? <RefreshCcwDot /> : <RefreshCwOff />}
                </Button>
              </div>
              {!hasValidPaymentMethod && (
                <Button
                  variant="outline"
                  onClick={async () => {
                    alert(
                      t("subscriptions.payment_methods.singular"),
                      <div className="flex flex-col gap-8">
                        <PaymentMethods />
                        <BillingInvoiceReceiverDetails />
                      </div>,
                      { size: "sm" },
                    );
                  }}
                  title={t(
                    "subscriptions.payment_methods.payment_method_required.title",
                  )}
                >
                  <CreditCard size={18} />
                  <TriangleAlert size={18} className="text-warning" />
                </Button>
              )}
              {showPurchaseButton && (
                <Button
                  className="rounded-full"
                  variant="secondary"
                  isLoading={isRefillingPrepaid}
                  onClick={async () => {
                    const isConfirmed = await confirm(
                      t("subscriptions.prepaid.title"),
                      <div className="flex flex-col items-center gap-8">
                        <PrepaidIndicator
                          currentAmount={prepaidAmount}
                          pendingAdjustment={prepaidSelectedAmount}
                        />
                        <p className="border-t border-slate-100 pt-4 text-center">
                          {isInvoiced
                            ? t("subscriptions.prepaid.confirmation.invoice", {
                                amount: prepaidSelectedAmount,
                              })
                            : t("subscriptions.prepaid.confirmation.card", {
                                amount: prepaidSelectedAmount,
                              })}
                        </p>
                      </div>,
                      { size: "xs" },
                    );

                    if (!isConfirmed) return;

                    await refillPrepaid();
                  }}
                >
                  <CreditCard size={18} />
                  {t("subscriptions.prepaid.purchase", {
                    amount: prepaidSelectedAmount,
                  })}
                </Button>
              )}
            </div>
          ) : willOverdraft ? (
            <div className="flex gap-2">
              <Lock className="text-slate-400" />
              <p className="max-w-[200px]">
                {t("subscriptions.prepaid.contact_admin")}
              </p>
            </div>
          ) : null}
        </div>
        {dialogElement}
      </div>
      {willOverdraft && (
        <div className="text-destructive">
          {t("documents.esign.no_funds_modal.title")}
        </div>
      )}
    </>
  );
};

export default PrepaidPanel;
